import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import api from '@/lib/api';

interface InputData {
  fileName: string;
  fileSize: number;
  mimeType: string;
  language?: string;
}

interface OutputData {
  type?: string;
  originalText?: string;
  processedText?: string;
  cleanedText?: string;
  error?: string;
  stack?: string;
}

interface Trace {
  traceId: string;
  startedAt: string;
  endedAt?: string;
  duration?: number;
  ipAddress?: string;
  userAgent?: string;
  httpMethod?: string;
  httpPath?: string;
  httpStatus?: number;
  userId?: string;
  appId?: string;
}

interface Span {
  spanId: string;
  traceId: string;
  parentSpanId?: string;
  startedAt: string;
  endedAt?: string;
  duration?: number;
  provider?: string;
  operation: string;
  method?: string;
  url?: string;
  statusCode?: number;
  success: boolean;
  errorMessage?: string;
  errorCode?: string;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  requestBody?: any;
  responseBody?: any;
  retryCount?: number;
  isRetry?: boolean;
  userId?: string;
  appId?: string;
}

interface Operation {
  id: string;
  traceId?: string;
  userId?: string;
  appId?: string;
  operation: string;
  inputType?: string;
  inputData?: InputData;
  outputType?: string;
  outputData?: OutputData;
  status: string;
  deletedAt?: string;
  createdAt: string;
  user?: {
    id: string;
    username: string;
    email: string;
    name: string;
  };
  application?: {
    id: string;
    appKey: string;
    name: string;
  };
}

interface TraceDetailResponse {
  trace: Trace;
  operation: Operation | null;
  spans: Span[];
}

const OPERATION_LABELS: Record<string, string> = {
  pipeline_clean: '精简整理',
  pipeline_creative: '灵感升华',
  chat: '对话',
  summary: '摘要生成',
  translation: '翻译',
  transcription: '语音转文字',
  text_processing: '文字优化',
  chat_completion: '对话生成',
};

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic (Claude)',
  openrouter: 'OpenRouter',
  xunfei: '讯飞',
  google: 'Google',
  deepseek: 'DeepSeek',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待处理',
  processing: '处理中',
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
};

interface ApiResponse {
  status: string;
  code: number;
  data: TraceDetailResponse;
  trace_id?: string;
}

export default function UserOperationDetail() {
  const { traceId } = useParams<{ traceId: string }>();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['user-operation', traceId],
    queryFn: async () => {
      console.log('[UserOperationDetail] Fetching trace:', traceId);
      const response = await api.get<ApiResponse>(
        `/admin/traces/${traceId}`
      );
      console.log('[UserOperationDetail] API Response:', response.data);
      console.log('[UserOperationDetail] Trace data:', response.data.data);
      return response.data.data;
    },
    enabled: !!traceId,
    retry: false,
    staleTime: 0,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getOperationLabel = (operation: string) => {
    return OPERATION_LABELS[operation] || operation;
  };

  const getProviderLabel = (provider: string) => {
    return PROVIDER_LABELS[provider] || provider;
  };

  const getStatusBadge = (status: string) => {
    const colors = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
    const label = STATUS_LABELS[status] || status;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors}`}>
        {label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">加载中...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-600 font-medium">
          加载失败
        </div>
        <div className="text-sm text-gray-600">
          {error instanceof Error ? error.message : '未知错误'}
        </div>
        <Link
          to="/user-operations"
          className="text-blue-600 hover:text-blue-800"
        >
          ← 返回列表
        </Link>
      </div>
    );
  }

  if (!data || !data.operation) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-gray-600">
          未找到操作记录
        </div>
        <Link
          to="/user-operations"
          className="text-blue-600 hover:text-blue-800"
        >
          ← 返回列表
        </Link>
      </div>
    );
  }

  const { trace, operation, spans } = data;

  // Calculate aggregated stats from spans
  const totalDuration = spans.reduce((sum, span) => sum + (span.duration || 0), 0);
  const totalTokens = spans.reduce((sum, span) => sum + (span.totalTokens || 0), 0);
  const childCount = spans.length;

  return (
    <div className="space-y-6">
      {/* 头部导航 */}
      <div className="flex items-center space-x-2">
        <Link
          to="/user-operations"
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          ← 返回列表
        </Link>
      </div>

      {/* 会员操作信息（父记录） */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              会员操作详情
            </h2>
            {getStatusBadge(operation.status)}
          </div>
          <div className="mt-1 text-sm text-gray-600">
            Operation ID: {operation.id} • Trace ID: {trace.traceId}
          </div>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* 基本信息 */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                操作时间
              </label>
              <div className="mt-1 text-sm text-gray-900">
                {formatDateTime(operation.createdAt)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                操作类型
              </label>
              <div className="mt-1 text-sm text-gray-900">
                {getOperationLabel(operation.operation)}
              </div>
            </div>

            {operation.user && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  用户
                </label>
                <div className="mt-1 text-sm text-gray-900">
                  {operation.user.name} ({operation.user.email})
                </div>
              </div>
            )}

            {operation.application && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  应用
                </label>
                <div className="mt-1 text-sm text-gray-900">
                  {operation.application.name} ({operation.application.appKey})
                </div>
              </div>
            )}
          </div>

          {/* 输入信息 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📤 输入
            </label>
            <div className="bg-gray-50 rounded-md p-4">
              {operation.inputType === 'AUDIO' && operation.inputData ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">🎵</span>
                    <div className="flex-1">
                      <div className="font-medium">{operation.inputData.fileName}</div>
                      <div className="text-sm text-gray-600">
                        {formatFileSize(operation.inputData.fileSize)} • {operation.inputData.mimeType}
                      </div>
                    </div>
                  </div>
                  {operation.inputData.language && (
                    <div className="text-sm text-gray-600">
                      语言: {operation.inputData.language}
                    </div>
                  )}
                </div>
              ) : (
                <pre className="text-xs text-gray-700 overflow-auto">
                  {JSON.stringify(operation.inputData, null, 2)}
                </pre>
              )}
            </div>
          </div>

          {/* 输出信息 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📥 输出
            </label>
            <div className="bg-gray-50 rounded-md p-4">
              {operation.status === 'failed' ? (
                <div className="space-y-2">
                  <div className="text-red-600 font-medium">
                    ❌ 处理失败
                  </div>
                  <div className="text-sm text-gray-700">
                    {operation.outputData?.error}
                  </div>
                  {operation.outputData?.stack && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-600 cursor-pointer">
                        查看错误堆栈
                      </summary>
                      <pre className="mt-2 text-xs text-red-600 overflow-auto">
                        {operation.outputData.stack}
                      </pre>
                    </details>
                  )}
                </div>
              ) : operation.outputType === 'TEXT' && operation.outputData ? (
                <div className="space-y-4">
                  {operation.outputData.originalText && (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        原始识别文字（STT）
                      </div>
                      <div className="bg-white p-3 rounded border text-sm text-gray-900">
                        {operation.outputData.originalText}
                      </div>
                    </div>
                  )}
                  {operation.outputData.processedText && (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        优化后文字（LLM）
                      </div>
                      <div className="bg-white p-3 rounded border text-sm text-gray-900">
                        {operation.outputData.processedText}
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(operation.outputData?.processedText || '')}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                      >
                        复制文字
                      </button>
                    </div>
                  )}
                </div>
              ) : operation.status === 'processing' || operation.status === 'pending' ? (
                <div className="text-blue-600">
                  ⏳ 处理中，请稍后刷新查看结果...
                </div>
              ) : (
                <pre className="text-xs text-gray-700 overflow-auto">
                  {JSON.stringify(operation.outputData, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 技术详情（子记录） */}
      {childCount > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                🔧 技术详情
              </h2>
              <div className="text-sm text-gray-600">
                总耗时: {formatDuration(totalDuration)} • 总 Token: {totalTokens || 0}
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="space-y-4">
              {spans.map((span, index) => (
                <div
                  key={span.spanId}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">
                            {getOperationLabel(span.operation)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {span.provider ? getProviderLabel(span.provider) : 'Unknown'} • {span.method || 'N/A'} {span.url || ''}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">状态码:</span>{' '}
                          <span className="font-medium">{span.statusCode || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">耗时:</span>{' '}
                          <span className="font-medium">{span.duration ? formatDuration(span.duration) : 'N/A'}</span>
                        </div>
                        {span.totalTokens && (
                          <div>
                            <span className="text-gray-600">Token:</span>{' '}
                            <span className="font-medium">{span.totalTokens}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Span ID:</span>{' '}
                          <span className="font-medium text-xs">{span.spanId}</span>
                        </div>
                      </div>

                      {span.errorMessage && (
                        <div className="mt-3 text-sm text-red-600">
                          ❌ {span.errorMessage}
                        </div>
                      )}

                      {span.promptTokens && span.completionTokens && (
                        <div className="mt-3 text-xs text-gray-600">
                          输入: {span.promptTokens} • 输出: {span.completionTokens}
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      {span.success ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ 成功
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          ✗ 失败
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 请求和响应详情 */}
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                    {/* 请求 */}
                    {span.requestBody && (
                      <div>
                        <details className="group">
                          <summary className="flex items-center cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                            <span className="mr-2">📤</span>
                            <span>请求体</span>
                            <span className="ml-2 text-xs text-gray-500">
                              (点击展开/收起)
                            </span>
                          </summary>
                          <div className="mt-2 bg-gray-50 rounded p-3">
                            <pre className="text-xs text-gray-800 overflow-auto max-h-60">
                              {JSON.stringify(span.requestBody, null, 2)}
                            </pre>
                          </div>
                        </details>
                      </div>
                    )}

                    {/* 响应 */}
                    {span.responseBody && (
                      <div>
                        <details className="group">
                          <summary className="flex items-center cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                            <span className="mr-2">📥</span>
                            <span>响应体</span>
                            <span className="ml-2 text-xs text-gray-500">
                              (点击展开/收起)
                            </span>
                          </summary>
                          <div className="mt-2 bg-gray-50 rounded p-3">
                            <pre className="text-xs text-gray-800 overflow-auto max-h-60">
                              {typeof span.responseBody === 'string'
                                ? span.responseBody
                                : JSON.stringify(span.responseBody, null, 2)}
                            </pre>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 无子记录时的提示 */}
      {childCount === 0 && operation.status === 'completed' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-blue-800">
            ℹ️ 该操作没有记录技术细节，或数据已被清理。
          </div>
        </div>
      )}
    </div>
  );
}
